
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Images, Activity, CreditCard, Crown, RefreshCw } from "lucide-react";
import { useUnifiedUserData } from "@/hooks/useUnifiedUserData";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const OptimizedDashboardStatsGrid = () => {
  const { profile, loading, error, refetch } = useUnifiedUserData();
  const [totalImages, setTotalImages] = useState<number | null>(null);
  const [monthlyImages, setMonthlyImages] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchCounts = async () => {
    if (!profile?.id) return;
    setIsRefreshing(true);
    try {
      // Total count
      const { count: total } = await supabase
        .from('generated_images')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id);
      setTotalImages(total ?? null);
  
      // Current month count
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const { count: monthCount } = await supabase
        .from('generated_images')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .gte('created_at', startOfMonth.toISOString());
      setMonthlyImages(monthCount ?? null);
  
      setLastUpdated(new Date().toISOString());
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    if (!profile?.id) return;

    // Live update counts when new images are saved for this user
    const channel = supabase
      .channel(`gen-images-counts-${profile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'generated_images',
        filter: `user_id=eq.${profile.id}`
      }, () => {
        fetchCounts();
        refetch(); // also refresh profile credits/usage if needed
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'generated_images',
        filter: `user_id=eq.${profile.id}`
      }, () => {
        fetchCounts();
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'generated_images',
        filter: `user_id=eq.${profile.id}`
      }, () => {
        fetchCounts();
      })
      .subscribe();

    // Refresh when tab regains focus/visibility
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchCounts();
        refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // Removed 5-second polling for better performance
    // Realtime subscriptions and visibility changes will keep data fresh

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-4">
              <Skeleton className="h-3 w-20 mb-1 bg-gray-600" />
              <Skeleton className="h-6 w-12 mb-1 bg-gray-500" />
              <Skeleton className="h-2 w-16 bg-gray-600" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
        <Card className="bg-red-500/10 backdrop-blur-md border-red-500/20">
          <CardContent className="p-4">
            <div className="text-center text-red-400">
              <Activity className="w-6 h-6 mx-auto mb-1" />
              <p className="text-xs">Failed to load user data</p>
              <button 
                onClick={refetch} 
                className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const usedThisMonth = (monthlyImages ?? profile.image_generations_used);
  const remainingThisMonth = Math.max(0, profile.monthly_limit - usedThisMonth);
  const isPayAsGo = profile.plan_type === 'pay_as_go';
  const isUnlimited = profile.monthly_limit === 999999;

  const getPlanName = (planType: string) => {
    switch (planType) {
      case 'free': return '🆓 Free Forever';
      case 'hobby': return '🎨 Hobby';
      case 'creator': return '🚀 Creator';
      case 'professional': return '💼 Professional';
      case 'business': return '🏢 Business';
      case 'pay_as_go': return '💳 Pay as You Go';
      case 'enterprise': return '🏆 Enterprise';
      default: return '🆓 Free Forever';
    }
  };

  const statsData = [
    {
      title: "Total Images",
      value: (totalImages ?? profile.image_generations_used).toString(),
      change: (totalImages ?? profile.image_generations_used) > 0 ? `${totalImages ?? profile.image_generations_used} generated` : "Start creating!",
      changeType: "positive" as const,
      icon: Images,
      color: "text-blue-400",
      bgGradient: "from-blue-500/10 to-blue-600/10",
      borderColor: "border-blue-500/20"
    },
    {
      title: "Monthly Limit",
      value: isUnlimited ? "Unlimited" : 
        isPayAsGo ? "Pay per use" : 
        profile.monthly_limit.toLocaleString(),
      change: isPayAsGo ? "$0.04/image" : 
        isUnlimited ? "No restrictions" :
        `${profile.monthly_limit.toLocaleString()} images/month`,
      changeType: "neutral" as const,
      icon: CreditCard,
      color: "text-green-400",
      bgGradient: "from-green-500/10 to-green-600/10",
      borderColor: "border-green-500/20",
      clickable: true
    },
    {
      title: "Used This Month",
      value: usedThisMonth.toString(),
      change: isUnlimited ? "No limits" : `of ${profile.monthly_limit.toLocaleString()}`,
      changeType: "neutral" as const,
      icon: Activity,
      color: "text-orange-400",
      bgGradient: "from-orange-500/10 to-orange-600/10",
      borderColor: "border-orange-500/20"
    },
    {
      title: "Remaining",
      value: isUnlimited ? "Unlimited" : remainingThisMonth.toLocaleString(),
      change: isUnlimited ? "No limits" : `${remainingThisMonth.toLocaleString()} left`,
      changeType: remainingThisMonth > 5 ? "positive" as const : "neutral" as const,
      icon: TrendingUp,
      color: remainingThisMonth > 5 ? "text-green-400" : "text-yellow-400",
      bgGradient: "from-purple-500/10 to-purple-600/10",
      borderColor: "border-purple-500/20"
    },
    {
      title: "Current Plan",
      value: getPlanName(profile.plan_type),
      change: profile.plan_type === 'free' ? "Upgrade available" : "Active subscription",
      changeType: profile.plan_type === 'free' ? "neutral" as const : "positive" as const,
      icon: Crown,
      color: profile.plan_type === 'free' ? "text-yellow-400" : "text-green-400",
      bgGradient: profile.plan_type === 'free' ? "from-yellow-500/10 to-yellow-600/10" : "from-green-500/10 to-green-600/10",
      borderColor: profile.plan_type === 'free' ? "border-yellow-500/20" : "border-green-500/20"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {statsData.map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Card 
            key={index} 
            className={`bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300 ${stat.borderColor} ${stat.clickable ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if (stat.clickable) {
                refetch(); // Refresh credits on click
                fetchCounts(); // Refresh counts as well
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-400">{stat.title}</p>
                  <div className="text-lg font-bold text-white">{stat.value}</div>
                  <div className="flex items-center text-xs">
                    <span className={
                      stat.changeType === 'positive' ? 'text-green-400' : 'text-gray-400'
                    }>
                      {stat.change}
                    </span>
                  </div>
                </div>
                 <div className="flex items-center gap-2">
                   <button
                     onClick={() => { refetch(); fetchCounts(); }}
                     aria-label="Refresh stats"
                     className="p-1 rounded hover:bg-white/10"
                     title={lastUpdated ? `Last updated ${new Date(lastUpdated).toLocaleTimeString()}` : 'Refresh'}
                   >
                     <RefreshCw className={`w-4 h-4 text-gray-300 ${isRefreshing ? 'animate-spin' : ''}`} />
                   </button>
                   <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.bgGradient}`}>
                     <IconComponent className={`w-5 h-5 ${stat.color}`} />
                   </div>
                 </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
